import { PeekyEmitter } from '@shared/peeky.emitter';
import $ from 'jquery';
import { PeekyActivator } from './peeky.activator';
import { GoogleAnalytics } from './peeky.analytics.ga';
import { PeekyElement } from './peeky.element';
import { PeekyInteractor } from './peeky.interactor';
import { PeekyState } from './peeky.state';
import { URL } from './peeky.url';

const RootKey = '_kk';
const ElementClass = '_kkElement';
const StateClass = '_kkState';
const NullStr = '_kkNull';
export { RootKey, ElementClass, StateClass, NullStr };

export type StringHash = {
  [key: string]: string;
}
export type PeekyElementHash = {
  [key: string]: PeekyElement;
}
export type BooleanHash = {
  [key: string]: boolean;
}
export type AnyHash = {
  [key: string]: any;
}
export type AjaxOptions = {
  url: string,
  method?: string,
  form?: HTMLFormElement,
  onProgress?: (e:ProgressEvent) => void,
  data?: { [key: string]: string | File },
  formData?: FormData
}

export type RootUrlParam = {
  [key: string]: ElementUrlParam
}

export type ElementUrlParam = {
  _arg?: string[],
  [key: string]: UrlParam | string | string[]
}

export type UrlParam = {
  [key: string]: UrlParam | string | string[]
}


type PeekyLocation = {
  url: string,
  param: RootUrlParam,
  serial: { [key: string]: string },
  _location: boolean
  protocol: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
  hashPath: string;
  hashSearch: string;
}

type Action = (this: Peeky, param: any) => Promise<void>;

type ActionList = {
  [key: string]: Action
}

type PeekyOptions = {
  $dom?: JQuery,
  DOM?: HTMLElement
}

type GotoParam = {
  [key: string]: any;
}


export class Peeky extends PeekyEmitter {
  $dom: JQuery;
  root: PeekyElement;
  rootKey: string;

  private _param: RootUrlParam;
  private _currentHash: string;
  _lastGoTo: any;

  _loading: BooleanHash = {};
  _preloading: BooleanHash = {};
  _currentUrl: string;
  _property: AnyHash = {};
  anonStateCounter = 0;

  _scrollTop: number;
  _scrollLeft: number;
  _winWidth: number;
  _winHeight: number;

  stripEmptyArgsRegex = new RegExp("[^=&]+=(0$|0&|&|$|" + NullStr + ")", 'g');

  static elementClass = ElementClass;
  private _gaInstance: GoogleAnalytics;

  constructor(options: PeekyOptions = {}) {
    super();
    if (options.$dom instanceof $) {
      this.$dom = options.$dom;
    } else {
      this.$dom = $(document.body);
    }

    // Add initial actions
    for (let action in actions) {
      this.bindAction(action, actions[action]);
    }

    this.root = new PeekyElement(RootKey, {
      initialDocument: true,
      $dom: this.$dom,
      site: this,
      init: false
    });

    window.addEventListener('DOMContentLoaded', async () => {
      if (window.parent && window.parent !== window) {
        let parentPk = <Peeky>(<any>window.parent).pk;
        if (parentPk) {
          parentPk.trigger('registerIframe', { window: window });
          requestAnimationFrame(() => {
            parentPk.trigger('iframeReady', { window: window });
          });
        }
      }

      // Get initial parameters for URL
      // This will be missing _arg parameters for all elements
      let uri = decodeURI(window.location.pathname + window.location.search);
      let parameters = this.urlToParam(uri);
      await this.root.triggerAction('initElement', {
        param: parameters,
        initialDocument: true
      });

      // Trigger "loaded" for all elements & states
      await this.root.triggerAction('loadedElement');
      
      window.onpopstate = (e: Event) => {
        let goTo = (e) => {
          this.goTo(window.location.href, {
            pushState: false,
            scrollTop: e.state?.scrollTop,
            scrollLeft: e.state?.scrollLeft
          });
        };
        window.onpopstate = goTo;
        
        // Avoid firing on initial load
        if (!window.history.state || window.history.state === null) {
          return;
        }
        goTo(e);
      };
        
      // Go to initial states (to trigger the required events)
      this.triggerAction('goTo', {
        location: parameters,
        initial: true
      });

      window.addEventListener('resize', () => {
        this._scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        this._scrollLeft = window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft;
        this._winHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        this._winWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        pk.trigger('resize', { winWidth: pk._winWidth, winHeight: pk._winHeight, scrollTop: pk._scrollTop, scrollLeft: pk._scrollLeft });
      });
    });

    // Error logging
    let reportingError = 0;
    window.addEventListener('error', async (event) => {
      if (reportingError) return;
      else reportingError++;
      let error = event.error;
      try {
        await reportError(error);
      } catch(e) {
      }
      reportingError--;
    });
    window.addEventListener("unhandledrejection", async (event) => {
      if (reportingError) return;
      else reportingError++;
      let error = event.reason;
      try {
        await reportError(error);
      } catch(e) {
      }
      reportingError--;
    });

    let reportError = async (error) => {
      let { UAParser } = await import('ua-parser-js');
      let parser = new UAParser.UAParser();
      let name = error.name ?? 'Error';
      let message = error.message;
      let stack = error.stack;
      let formData = new FormData();
      let browser = parser.getBrowser();
      let os = parser.getOS();

      formData.append('_kk.reportError.name', name);
      formData.append('_kk.reportError.message', message);
      formData.append('_kk.reportError.stack', stack);
      formData.append('_kk.reportError.clientOsName', os.name);
      formData.append('_kk.reportError.clientOsVersion', os.version);
      formData.append('_kk.reportError.clientBrowserName', browser.name);
      formData.append('_kk.reportError.clientBrowserVersion', browser.version);
      formData.append('_kk.reportError.clientBrowserWidth', window.innerWidth + '');
      formData.append('_kk.reportError.clientBrowserHeight', window.innerHeight + '');
      formData.append('_kk.reportError.url', window.location.href);

      await this.ajax({
        url: '',
        formData: formData,
        method: 'POST'
      });
    };
  }

  /**
   * 
   * @param options 
   * @returns 
   */
  async ajax(options: AjaxOptions): Promise<XMLHttpRequest> {
    let form = options.form;
    let formData: FormData;
    let xhr = new XMLHttpRequest();
    let url: string;
    let method: string;

    if (options.formData instanceof FormData) {
      formData = options.formData;
    } else {
      formData = new FormData(options.form);
    }

    if (options.url) {
      url = options.url;
    } else if (form && form.getAttribute('action')) {
      url = form.getAttribute('action') || '';
    } else {
      url = '';
    }
    if (options.method) {
      method = options.method;
    } else if (form && form.getAttribute('method')) {
      method = form.getAttribute('method') || 'GET';
    } else {
      method = 'GET';
    }

    if (typeof(options.data) === 'object') {
      for (let key in options.data) {
        formData.append(key, options.data[key]);
      }
    }

    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        resolve(xhr);
      };
      xhr.onerror = () => {
        resolve(xhr);
      };
      if (typeof(options.onProgress) === 'function') {
        xhr.onprogress = options.onProgress;
      }
      xhr.open(method, url, true);
      xhr.send(formData);
    });
  }

  async goTo(location: string | PeekyLocation, options: GotoParam) {
    await this.triggerAction('goTo', { location: location, options: options });
  }

  async load(location: string | PeekyLocation, options: GotoParam) {
    await this.triggerAction('load', { location: location, options: options });
  }

  serialize(inparam: UrlParam, relative = false) {
    let param: UrlParam;
    if (!inparam) {
      param = this.param();
    }
    if (relative) {
      param = $.extend(true, {}, this.param(), inparam);
    } else {
      param = inparam;
    }
    return this.paramToQuery(param);
  }

  param(key?: string): RootUrlParam {
    let param: RootUrlParam = {};
    // Collect state from all elements
    function nextElement(this: PeekyElement) {
      let state = this.state();
      if (!state) {
        return;
      }
      if (key && key === this.key) {
        return param = state.param();
      }
      else if (!key) {
        param[this.key] = state.param();
      }
      for (let k in state.children) {
        nextElement.call(state.children[k]);
      }
    };
    nextElement.call(this.root);
    return param;
  }


  // @defaults: 	undefined: Keep default path arguments that exist
  // 				true: Keep and add unexisting defaults
  //				false: remove defaults
  urlToParam(url: string | Location, relative = false, defaults?: boolean): RootUrlParam {
    if (url instanceof Location) {
      url = url.href;
    }
    if (typeof('url') !== 'string') {
      return {};
    }
  
    let urlObj = new URL(url);
    let param = {};
    let queryParam = this.queryToParam(urlObj.search.substring(1)) || {};
    $.extend(true, param, queryParam);
    let pathParam = this.pathToParam(urlObj.pathname, defaults, param) || {};
    $.extend(true, param, pathParam);
    
    // If relative, add queries (not paths)
    if (relative) {
      let currentParam = $.extend(true, {}, this.param());
      if (currentParam._kk) {
        delete(currentParam._kk);
      }
      // Remove paths
      for (let k in currentParam) {
        if (typeof(currentParam[k]) === 'object') {
          if (currentParam[k]._arg) {
            delete(currentParam[k]._arg);
          }
          if (Object.keys(currentParam[k]).length <= 0) {
            delete(currentParam[k]);
          }
        }
      }
      param = $.extend(true, currentParam, param);
    }
    
    return param;
  }

  /**
   * 
   * @param path 
   * @param defaults  undefined: Keep default path arguments that exist
   *                  true: Keep and add unexisting defaults
   *                  false: remove defaults
   * @param incoming 
   * @returns 
   */
  pathToParam(path: string | string[], defaults?: boolean, incoming?: RootUrlParam) {
    if (!path) {
      return {};
    }
    let result = incoming || {};
    let highestIndex = 0;
    let removable: number[] = [];
    let unremovable: number;

    if (typeof(path) === 'string') {
      // Remove rootPath
      let rootPath = this.property('rootPath') || '/';
      path = path.replace( new RegExp('^' + rootPath), '');
      // Remove trailing & leading & double slashes
      path = path.replace(/^\//, '').replace(/\/$/, '').replace(/\/[\/]+/g, '/').split('/');
    }
  
    function recurse(this: PeekyElement, skip: number) {
      if (this.catchPath() > 0) {
        if (!this.catchPathTreeCompare(path)) {
          result[this.key] = this.param(true);
        } else {
          let pathLevel = this.pathLevel() - this.catchPath();
          for (let i=0; i<this.catchPath(); i++) {
            let arg = path[pathLevel+i];
            let defaultArg = this.defaultPath[i];
            let lockPath = this.lockPath[i];
            let multiPath = typeof(this.multiPath) === 'boolean' ? this.multiPath : this.multiPath[i];
            if (!result[this.key]) {
              result[this.key] = {};
            }
            if (typeof(result[this.key]._arg) !== 'object') {
              result[this.key]._arg = [];
            }

            // An argument is given
            if (lockPath) {
              result[this.key]._arg[i] = lockPath;
              unremovable = Math.max(pathLevel+i, unremovable || 0);
            }
            else if (arg) {
              result[this.key]._arg[i] = arg;
              result[RootKey]._arg[pathLevel+i] = arg;
              unremovable = Math.max(pathLevel+i, unremovable || 0);
            }
            // There is a default argument, and no given argument
            else if (defaultArg && !arg && defaults === true) {
              result[this.key]._arg[i] = defaultArg;
              result[RootKey]._arg[pathLevel+i] = defaultArg;
              unremovable = Math.max(pathLevel+i, unremovable || 0);						
            } else if (defaults === false) {
              removable.push(pathLevel+i);
            }

            // Check if element should merge multiple paths to one state
            // When true, match always
            // When array, match if value is found in array
            if (multiPath === true || (Array.isArray(multiPath) && multiPath.indexOf(result[this.key]._arg[i] || '') !== -1)) {
              result[this.key]._arg[i] = '__pkmulti';
            }

            highestIndex = pathLevel+i;
          }
        }
      }

      // Recurse *current state* from generated parameters
      for (let i=0; i<this.states.length; i++) {
        if (this.key === RootKey || this.states[i].equals(result[this.key])) {
          for (let k in this.states[i].children) {
            recurse.call(this.states[i].children[k], skip+this.catchPath());
          }
        }
      }
    };
    
    result[RootKey] = {};
    result[RootKey]._arg = { ...path };

    // Add paths to all existing elements
    if (this.root) {
      recurse.call(this.root, 0);
    }
    
    // Remove "default" values from the end of URL
    if (defaults === false && removable.length > 0) {
      let remove: number;
      highestIndex = Math.max(highestIndex, path.length) - 1;
      while (highestIndex <= (remove = removable.pop()) && remove > unremovable) {
        result[RootKey]._arg[remove] = undefined;
      }
    }

    return result;
  }


  // Return an object containing:
  // - url: the complete URL of the request
  // - param: the complete set of parameters for the request
  // - serial: {} an object containing each elements serial
  // - _location: a "marker" that says this is a location object
  // if "Relative" is specified, the location will be merged with the current location
  location(location: Location | PeekyLocation | RootUrlParam | string, relative = false): PeekyLocation {
    let url: string;
    let parameters: RootUrlParam;
    let serial: { [key: string]: string };

    if (typeof(location) === 'undefined') {
      location = this.param();
    }
    else if (location instanceof Location) {
      location = location.href;
    }

    if (typeof(location) === 'string') {
      // Remote location
      if (/http[s]?\:\/\//.test(location)) {
        url = location;
        parameters = {};
        serial = {};
      }
      if (!relative) {
        parameters = this.urlToParam(location);
      }
      else {
        parameters = this.urlToParam(location, true);      
      }
    }
    else if (typeof(location) === 'object') {
      if (location._location && !relative) parameters = (<PeekyLocation>location).param;
      else if (location._location) parameters = $.extend(true, {}, this.param(), location.param);
      else if (!relative) parameters = <RootUrlParam>location;
      else parameters = $.extend(true, {}, this.param(), location);
    }

    // Pass parameters through URL convertor once, to add _arg parameters
    parameters = this.urlToParam(this.paramToUrl(parameters));
    
    // Create serial for each element
    serial = {};
    for (let k in parameters) {
      serial[k] = this.serialize(parameters[k]);
    }

    // Generate URL
    if (typeof(url) === 'undefined') {
      url = this.paramToUrl(parameters);
    }
    let urlObj = new URL(url);


    // Create final object
    location = {
      url: url,
      param: parameters,
      serial: serial,
      _location: true,
      ...urlObj
    };
    
    return location;
  }


  queryToParam(query: string) {
    let statements = query.split('&');
    let param = {};

    for (let i in statements) {
      if (typeof(statements[i]) !== 'string') {
        continue;
      }
      let splitStatement = statements[i].split('=');
      let parts = splitStatement[0].split(/\./);
      let value = splitStatement[1];
      
      // Generate object-structure
      let currentRoot = param;
      for (let j=0; j<parts.length-1; j++) {
        if (typeof(currentRoot[parts[j]]) !== 'object') {
          currentRoot[parts[j]] = {};
        }
        currentRoot = currentRoot[parts[j]];
      }
      
      // Assign value to leaf
      currentRoot[parts[parts.length-1]] = value;
    }
    
    return param;
  }


    // If "ajax" is specified, return the URL that should be used for pushState / Hash
  // Like rootPath/#!foo/bar/ or rootPath/foo/bar/
  paramToUrl(param?: RootUrlParam, clean?) {
    param = $.extend(true, {}, param || this.param());
    
    // Generate path first
    let url = this.paramToPath(param);
    
    // Remove all metadata
    if (clean) {
      delete(param[RootKey]);
    }
    for (let k in param) {
      delete(param[k]._arg);
    }
    
    // Append query without metadata
    let query = this.paramToQuery(param);
    if (query) {
      url += '?' + query;
    }
    
    return url;
  }


  paramToPath(param): string {
    let rootPath: string = this.property('rootPath') || '/';
    if (!param) {
      param = this.param();
    }

    let root = param[RootKey];
    if (!root || !root._arg) {
      return rootPath;
    }
    
    let pathArray = [];

    for (let k in root._arg) {
      if (root._arg[k] === '') {
        continue;
      }
      if (root._arg[k] === NullStr) {
        continue;
      }
      pathArray[parseInt(k, 10)] = root._arg[k];
    }
    
    let pathString = '/' + pathArray.join('/') + '/';
    pathString = rootPath + pathString;
    pathString = pathString.replace(/\/\/+/g, '\/');
    return pathString;
  }
  
  
  //
  paramToQuery(param) {
    if (!param) {
      param = this.param();
    }
    let array = [];
    function recurse(param, string) {
      for (let k in param) {
        if (typeof(param[k]) === 'object') {
          recurse(param[k], string + k + '.' );
        }
        else if (typeof(param[k]) === 'undefined') {
          continue;
        }
        else {
          array.push(string + k + '=' + param[k]);
        }
      } 
    };
    recurse(param, '');

    return array.sort().join('&') || '';
  }


  underRoot(url?: string) {
    if (typeof(url) === 'undefined') {
      url = window.location.href.toString();
    }

    // Split url into parts
    let splitUrl = new URL(url);
    
    // Different protocol
    if (splitUrl.protocol !== '' && splitUrl.protocol !== window.location.protocol) {
      return false;
    }
    // Different hostname
    if (splitUrl.hostname !== '' && splitUrl.hostname !== window.location.hostname) {
      return false;
    }
    // Different port
    if (splitUrl.port !== window.location.port) {
      return false;
    }
    
    // Different root path
    let rootPath = this.property('rootPath') || '/';
    let testPath = new RegExp("^" + rootPath);
    if (!testPath.test(splitUrl.pathname)) {
      return false;
    }

    return true;
  }


  atRoot(url) {
    let splitUrl = new URL(url);

    // Different hostname
    if (splitUrl.hostname !== '' && splitUrl.hostname !== window.location.hostname) {
      return false;
    }
    // Different port
    if (splitUrl.port !== window.location.port) {
      return false;
    }
    
    // Has pushState support, all roots are supported
    if (window.history.pushState) {
      return true;
    }
    
    // Test 
    let rootPath = this.property('rootPath') || '/'
    let testPath = new RegExp("^" + window.location.pathname);
    if (testPath.test(rootPath)) {
      return true;
    }
    
    return false;
  }


  // Key/value implementations
  property(key: string | object, val?: any) {
    if (!this._property) {
      this._property = {};
    }
    if (typeof(key) === 'object') {
      val = key;
      for (let subkey in val) {
        this.property(subkey, val[subkey]);
      }
      return;
    } 
    if (typeof(val) === 'undefined') {
      return this._property[key];
    }
    this._property[key] = val; 
  }


  $find(selector: string) {
    return this.$dom.find(selector).not('._detaching ' + selector); // TODO: This will not work with comma-separated selectors
  }


  compareStates(a: PeekyState | ElementUrlParam | string, b: PeekyState | ElementUrlParam | string): boolean {
    if (a instanceof PeekyState) a = a.serialize();
    else if (typeof(a) === 'object') a = this.paramToQuery(a);
    else if (typeof(a) === 'undefined') a = '';
    else if (typeof(a) !== 'string') return false;
    
    if (b instanceof PeekyState) b = b.serialize();
    else if (typeof(b) === 'object') b = this.paramToQuery(b);
    else if (typeof(b) === 'undefined') b = '';
    else if (typeof(b) !== 'string') return false;

    // First check naively
    if (a === b) return true;

    // Remove empty arguments
    a = a.replace(this.stripEmptyArgsRegex, '').replace(/(&|\?)$/, '');
    b = b.replace(this.stripEmptyArgsRegex, '').replace(/(&|x\?)$/, '');
    
    return (a === b);
  }


  element(searchKey): PeekyElement {
    function search(this: PeekyElement) {
      let key: string = this.key;
      let state: PeekyState = this.state();
      let element: PeekyElement;

      if (key === searchKey) {
        return this;
      }

      for (let k in state.children) {
        element = search.call(state.children[k]);
        if (element) {
          return element;
        }
      }
    };
    let result = search.call(this.root);
    return result;
  }


  interactor(selector: string, options?) {
    return new PeekyInteractor(this, selector, options);
  }


  activator(selector: string) {
    return new PeekyActivator(this, selector);
  }


  ga(debug: boolean, addHostname: boolean, ...trackers: string[]): void;
  ga(debug: boolean, ...trackers: string[]): void;
  ga(...trackers: string[]): void;
  ga(...args: any[]) {
    if (!this._gaInstance) {
      this._gaInstance = new GoogleAnalytics(this);
    }
    this._gaInstance.track(...args)
  }
  
}


let actions: ActionList = {
  goTo: async function(this: Peeky, param: GotoParam) {
    let options = param.options = param.options || {};
    let location = param.location = this.location(param.location || this.param(), options.relativeLocation);
    let actions: ActionList = param.actions || {};
    let goToState = new PeekyEmitter();
    let cancelled = false;

    param._stateEmitter = goToState;

    // Check that the requested location is under root
    // Also detect files with an extension, they should be fetched without ajax
    if (!this.underRoot(location.url) || (/\.[a-zA-Z0-9]+$/).test(location.url)) {
      if (options.popupRemote === true) {
        window.open(location.url, '_blank');
      } else {
        window.location.assign(location.url);
      }
      throw undefined;
    }
    
    //Check if we are at the required root (if pushstate is not supported)
    if (!this.atRoot(location.url)) {
      location.url = location.url.replace(new RegExp('^(' + (this.property('rootPath') || '/') + ')(.*)'), '$1#!$2');
      window.location.assign(location.url);
      throw undefined;
    }

    for (let key in actions) {
      goToState.oneAction(key, actions[key]);
    }
    goToState.on('cancel', () => {
      cancelled = true;
    });
    
    this._lastGoTo = param;

    // Check if a new goTo has been called between each step		
    let checkLast = async (eventName?: string) => {
      if (this._lastGoTo !== param) {
        throw undefined;
      }
      if (cancelled && eventName === 'load') {
        // Cancel switchState
        let switchElements: { [key: string]: PeekyElement } = param.switchElements;
        let switchElementParams: { [key: string]: PeekyState } = param.switchElementParams;
        for (let key in switchElements) {
          let element = switchElements[key];
          let stateParam = switchElementParams[key];
          await element.triggerAction('cancelSwitchState', stateParam);
        }
        throw undefined;
      }
    };
    
    // Prepare switch for all affected elements
    param = await this.triggerAction('prepareSwitch', param);
    await checkLast('prepareSwitch');
    
    // Load elements (if required)
    param = await this.triggerAction('load', param);
    await checkLast('load');

    // When all elements are loaded, check wich ones we need to replace 
    param = await this.triggerAction('showStates', param);
    await checkLast('showStates');
        
    // Push the new state
    param = await this.triggerAction('pushState', param);
    await checkLast('pushState');

    // Mark as switched
    param = await this.triggerAction('switched', param);
    await checkLast('switched');
  },
  
  
  load: async function(this: Peeky, param) {
    let options = param.options = param.options || {};
    let location = param.location = this.location(param.location || this.param(), options.relativeLocation);
    let serial = location.serial;
    let load: string[] = param.load = [];
    let reload = {};
    let element: { [key: string]: PeekyElement } = param.element = {};
    
    // Turn reload string into indexed object
    if (options.reload === 'all') {}
    else if (options.reload === 'changed') {}
    else if (typeof(options.reload) === 'string') options.reload = options.reload.split(',');

    if (Array.isArray(options.reload)) {
      for (let i=0; i<options.reload.length; i++) {
        let element = options.reload[i];
        reload[element] = true;
      }
    }

    if (typeof(options.reload) === 'undefined') {
      options.reload = 'none';
    }

    // Search all elements to make sure they have the required state
    // The root will only contain one state wich has global parameters like _arg.0, _arg.1 etc., and should never be reloaded
    let promises = [];
    function search(this: PeekyElement, root?: boolean) {
      let key = this.key;
      let state = this.state( root ? undefined : (serial[key] || '') );
      let loadthis = false;
      element[key] = this;

      // We don't want to replace root since it contains all <head/>, <body/> etc
      if (!root) {
        // If state doesn't exist, load it
        if (!state) loadthis = true;
        // If *this* element should be reloaded
        else if (reload[key]) loadthis = true;
        // If *all* elements should be reloaded
        else if (options.reload === 'all') loadthis = true;
        // If elements that are to be changed should be reloaded
        else if (options.reload === 'changed' && state !== this.state()) loadthis = true;
      }
      // Don't search further if this element will be replaced
      if (loadthis) {
        load.push(this.key);
        let promise = this.triggerAction('loadState', {
          ...param,
          state: state,
          serial: serial[key],
          location: location
        });
        promises.push(promise);
      } else {
        for (var k in state.children) {
          search.call(state.children[k]);
        }
      }
    };
    search.call(this.root, true);
    await Promise.all(promises);


    // Add kk.view
    let sendParam = { ...location.param };
    if (!sendParam[RootKey]) {
      sendParam[RootKey] = {};
    }
    sendParam[RootKey]._view = load.join(',');
    
    if (load.length > 0) {
      let url = param.url = this.paramToUrl(sendParam);

      // We are already preloading the requested URL
      if (this._loading[url] || this._preloading[url]) {
        if (!param.preLoad) {
          this._loading[url] = this._preloading[url];
        }

        await new Promise<void>((resolve) => {
          let check = (innerParam) => {
            if (innerParam.url !== url) {
              this.once('postLoad', check);
              return;
            }
            this._preloading[url] = undefined;
            this._loading[url] = undefined;
            resolve();
          };
          this.once('postLoad', check);
        });
        return;
      }

      if (param.preLoad) {
        //that._preloading[url] = ajaxCall;
        this._preloading[url] = true;
      } else {
        //that._loading[url] = ajaxCall;
        this._loading[url] = true;
      }

      // Remove _preloading and _loading markers
      let check = (param) => {
        if (param.url !== url) {
          this.once('postLoad', check);
          return;
        }
        this._preloading[url] = undefined;
        this._loading[url] = undefined;
      }
      this.once('postLoad', check);
      
      // Load url
      if (!options.ajax) {
        options.ajax = {};
      }
      options.ajax.url = url;

      let xhr = await this.ajax(options.ajax);
      let jsonSrc: string = xhr.responseText;
      let json = JSON.parse(jsonSrc);
      param.metadata = json.m;
      param.elementdata = json.e;

      await this.triggerAction('fetchedData', param);
      await this.triggerAction('createStates', param);
    }

  },

  
  createStates: async function(param) {
    let load = param.load;
    let element = param.element;
    let metadata = param.metadata;
    let elementdata = param.elementdata;
    for (let i=0; i<load.length; i++) {
      param.stateInfo = {
        element: element[load[i]],
        data: elementdata[load[i]],
        meta: metadata,
        param: param.location.param
      };
      await this.triggerAction('createState', param);
      param.stateInfo = undefined;
    }
  },
  
  
  prepareSwitch: async function(this: Peeky, param) {
    let elements: PeekyElementHash = param.elements = {}; // Cache the element for pushState
    let switchElements: PeekyElementHash = param.switchElements = {};	// Index elements that will be switched in this pass
    let switchElementParams: { [key: string]: PeekyState } = param.switchElementParams = {};
    let switchElementRoots: PeekyElementHash = param.switchElementRoots = {};
    let options = param.options = param.options || {};
    let location = param.location = this.location(param.location || this.param(), options.relativeLocation);
    let serial = location.serial;

    // Show the required elements
    let promises: Promise<any>[] = [];
    async function search(this: PeekyElement, root: PeekyElement, found: PeekyElement | boolean, parentParam) {
      let state: PeekyState;
      let switchthis = false;
      elements[this.key] = this;
      state = this.state();
      parentParam = {
        ...param,
        serial: serial[this.key] || '',
        switchElement: found || this,
        child: !!found,
        parentParam: parentParam, // References previous parentParam
        serials: serial,
        location: location
      }
      await this.triggerAction('prepareSwitchSearch', parentParam);
      
      if (!root && !found && !state.equals(serial[this.key] || '')) {
        switchthis = true;
      } else if (!root && options.reload === 'all') {
        switchthis = true;
      }
      if (switchthis) {
        let elementParam = { state: state, ...parentParam };
        let promise = this.triggerAction('prepareSwitchState', elementParam);
        promises.push(promise);
        switchElements[this.key] = this;
        switchElementParams[this.key] = elementParam;
        if (!found) {
          switchElementRoots[this.key] = this;
        }
        found = found || this;
      }
      for (let k in state.children) {
        await search.call(state.children[k], false, found, parentParam);
      }
    };
    await search.call(this.root, true, undefined, {});
    await Promise.all(promises);
  },
  
  
  showStates: async function(this: Peeky, param) {
    let elements: PeekyElementHash = param.elements = {}; // Cache the element for pushState
    let options = param.options = param.options || {};
    let location = param.location = this.location(param.location || this.param(), options.relativeLocation);
    let serial = location.serial;
    let reload = {};
    
    // Remove _kk._view (ugly workaround)
    if (location.param[RootKey]) {
      delete(location.param[RootKey]._view);
    }
    
    // Make an index of elements to reload
    if (Array.isArray(options.reload)) {
      for (let i=0; i<options.reload.length; i++) {
        reload[options.reload[i]] = true;
      }
    }

    // Show the required elements
    let promises = [];
    function search(switchingParent) {
      let state: PeekyState;
      let empty: boolean;
      elements[this.key] = this;
      if (this === this.root) {
        state = this.state();
        this.currentState.clear().param(location.param[this.key]);
        let promise = this.triggerAction('showState', {
          ...param,
          state: this.currentState
        });
        promises.push(promise);
      } else {
        state = this.state(serial[this.key] || '');
        var shouldSwitch = (state !== this.currentState);
        empty = (options.reload === 'all' || (options.reload === 'changed' && shouldSwitch) || !!reload[this.key]);
        if (switchingParent || empty || shouldSwitch || param.initial) {
          let promise = this.triggerAction('showState', {
            ...param,
            state: state,
            empty: empty,
            switchingParent: switchingParent
          });
          promises.push(promise);
        }
        switchingParent = switchingParent || shouldSwitch ? state : undefined;
      }
      for (let k in state.children) {
        search.call(state.children[k], switchingParent);
      }
    };
    search.call(this.root);
    await Promise.all(promises);
  },
  
  
  pushState: async function(this: Peeky, param) {
    let options = param.options = param.options || {};
    let location = param.location = this.location(param.location || this.param(), options.relativeLocation);
    let allParameters = location.param;
    let elements: PeekyElementHash = param.elements;
    let parameters = {};
    let nopush = this.property('noPushState') || false;
    
    // Only keep parameters that are actually usable (strip the ones for non-existing elements)
    for (let k in elements) {
      // Don't push state for elements that specify this
      if (elements[k].property('pushState') === false) {
        continue;
      }
      // Copy parameters
      if (allParameters[k]) {
        parameters[k] = allParameters[k];
      }
    }
    let url = this.paramToUrl(parameters, true);
    
    // If _currentUrl doesn't exist, this is the initial push (from page load), and we shouldn't push it again
    if (options.pushState === false || !this._currentUrl || this._currentUrl === url) {
      nopush = true;
    }

    this._currentUrl = url;

    // Split the url into segments and generate the path
    let urlObj = new URL(url);
    let path = param.pushPath = urlObj.pathname + urlObj.search;
    
    // Check if we should push state
    if (!nopush) {
      if (window.history.pushState) {
        window.history.pushState(parameters, '', path);
      }
    }
  },
  
  
  createState: async function(this: Peeky, param) {
    if (!param || !param.stateInfo||!param.stateInfo.element) {
      return;
    }
    await param.stateInfo.element.triggerAction('createState', param.stateInfo);
  }
};


/*
var Prototype = Peeky.Prototype = {
  
  loader: function(load, done) {
    var counter = 0
    , waiting = false
    , displaying = false;
    this.on('loadState', function() {
      if (!displaying && waiting && typeof(load) === 'function') {
        displaying = true;
        load.call(this);
      }
      counter++;
      function loadedState() {
        counter--;
        if (counter === 0 && typeof(done) === 'function') {
          displaying = false;
          waiting = false;
          done.call(this);
        }
        this.off('loadedState', loadedState);
        this.off('cancelLoadState', loadedState);
      };
      this.one('loadedState', loadedState);
      this.one('cancelLoadState', loadedState);
    });
    this.on('prepareSwitch', function() {
      waiting = true;
      if (counter > 0 && !displaying) {
        displaying = true;
        load.call(this);
      }
    });
    this.on('switched', function() {
      waiting = false;
    });
  },

  
};
*/